import type { ReactElement } from 'react';

import { StatusBadge } from '@/features/master-list/components/StatusBadge';
import { effectiveStatusBadge } from '@/features/master-list/document-status';
import { formatVersion } from '@/features/master-list/review-date';

import type { DossierEntry } from '../types';
import { formatDate } from '@/lib/time';

interface Props {
  entry: DossierEntry;
  /** Responsable primario (rol elaborador), ya resuelto; null → "Sin asignar". */
  primaryResponsibleName: string | null;
  /** El documento tiene más de una norma vinculada (badge "multinorma"). */
  multinorm: boolean;
  /** La versión efectiva está firmada y bloqueada (estampa "sellado"). */
  sealed: boolean;
  /** G4: la etapa de capacitación está abierta (estado derivado "En capacitación"). */
  inTraining?: boolean;
}

export function DossierHeader({
  entry,
  primaryResponsibleName,
  multinorm,
  sealed,
  inTraining = false,
}: Props): ReactElement {
  const badge = effectiveStatusBadge(entry.status, entry.nextReviewAt, new Date(), inTraining);
  const stampRed =
    badge.cls === 'b-red' || entry.status === 'obsoleto' || entry.status === 'anulado';
  const version = formatVersion(entry.version, entry.status);

  return (
    <div className="card rise" style={{ marginBottom: 16 }}>
      <div className="cb dossier-head">
        <div style={{ flex: 1 }}>
          <div className="rowb" style={{ gap: 9 }}>
            <span className="code" style={{ fontSize: 13, color: 'var(--accent-deep)' }}>
              {entry.code} · {version}
            </span>
            <StatusBadge
              status={entry.status}
              nextReviewAt={entry.nextReviewAt}
              inTraining={inTraining}
            />
            {multinorm && <span className="badge b-info">multinorma</span>}
          </div>
          <h1 style={{ fontSize: 26, margin: '6px 0' }}>{entry.title ?? entry.code}</h1>
          <div className="kvs kvs-row" style={{ marginTop: 14, maxWidth: 760 }}>
            <div className="kv">
              <div className="kk">Proceso</div>
              <div className="vv">{entry.processArea.name || '—'}</div>
            </div>
            <div className="kv">
              <div className="kk">Responsable</div>
              <div className="vv">{primaryResponsibleName ?? 'Sin asignar'}</div>
            </div>
            <div className="kv">
              <div className="kk">Vigente desde</div>
              <div className="vv">{formatDate(entry.effectiveDate)}</div>
            </div>
            <div className="kv">
              <div className="kk">Próx. revisión</div>
              <div className="vv">{formatDate(entry.nextReviewAt)}</div>
            </div>
          </div>
        </div>
        <div className={`estampa${stampRed ? ' red' : ''}`}>
          <div className="s1">Estado</div>
          <div className="s2">{badge.label}</div>
          <div className="s3">
            {version}
            {sealed ? ' · sellado' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
